import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { getRoles } from './types';
import { TrackPage } from './TrackPage';
import { useOnboardingTranslation } from '../../translations';
import { createOnboardingApiClient } from '../../api/onboardingApi';

export const TrackPageRoute = () => {
  const { roleId } = useParams<{ roleId: string }>();
  const navigate = useNavigate();
  const { t } = useOnboardingTranslation();
  const roles = useMemo(() => getRoles(t), [t]);
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const apiClient = useMemo(
    () => createOnboardingApiClient({ discoveryApi, fetchApi }),
    [discoveryApi, fetchApi],
  );

  const role = roles.find(r => r.id === roleId);

  const [completedJourneys, setCompletedJourneys] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('onboarding_completed_journeys');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const [npsCompleted, setNpsCompleted] = useState(false);

  // Load progress from the database on mount
  const loadedFromDb = useRef(false);
  useEffect(() => {
    if (loadedFromDb.current) return;
    loadedFromDb.current = true;

    apiClient.getProgress().then(progress => {
      if (progress.completed_journeys && progress.completed_journeys.length > 0) {
        setCompletedJourneys(prev => {
          // Merge DB data with localStorage (union)
          const merged = Array.from(new Set([...prev, ...progress.completed_journeys]));
          localStorage.setItem('onboarding_completed_journeys', JSON.stringify(merged));
          return merged;
        });
      }
      if (progress.nps_completed) {
        setNpsCompleted(true);
      }
    }).catch(() => {
      // Fallback: keep localStorage data
    });
  }, [apiClient]);

  useEffect(() => {
    if (!role) {
      navigate('/onboarding');
    }
  }, [role, navigate]);

  if (!role) return null;

  const handleCompleteJourney = (journeyId: string) => {
    setCompletedJourneys(prev => {
      const updated = [...prev, journeyId];
      localStorage.setItem('onboarding_completed_journeys', JSON.stringify(updated));
      return updated;
    });

    // Also persist to the database
    apiClient.completeJourney(journeyId, roleId).catch(() => {
      // DB save failed silently — localStorage already updated
    });
  };

  return (
    <TrackPage
      role={role}
      completedJourneys={completedJourneys}
      onCompleteJourney={handleCompleteJourney}
      apiClient={apiClient}
      npsCompleted={npsCompleted}
      onNpsCompleted={() => {
        setNpsCompleted(true);
      }}
    />
  );
};
